/* eslint-disable react/require-default-props */
import React, {useCallback, useState} from 'react';
import {TouchableOpacity, View} from 'react-native';
import {get} from 'lodash';

import Text from '/component/base/Text';
import {getCandidateSource} from '/component/partial/report/DateAddedComponent/DateAddedComponent.util';
import {Reports} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {getDateUtilDefaultInstance} from '/util/date.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './DateAddedComponent.style';

interface DateAddedComponentProps {
  item: {
    createdTs: string;
    source: string;
  };
  onPress?: () => void;
}

export const DateAddedComponent: React.FC<DateAddedComponentProps> = ({
  item = null,
  onPress,
}) => {
  const dateAdded = get(item, 'createdTs', '');
  const source = get(item, 'source', '');
  const displaySource = getCandidateSource(source)?.name || 'Leads Pool';

  const [isFocused, setFocused] = useState(false);

  const onFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const onBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const formattedDate = getDateUtilDefaultInstance().formatDate(
    dateAdded,
    'MMMM d, yyyy',
  );

  // Create a simple accessibility label
  const accessibilityLabel = `Added ${formattedDate} from ${displaySource}`;

  const textContent = (
    <View style={styles.container} accessible={false}>
      <Text
        style={styles.textWrapper}
        allowFontScaling
        maxFontSizeMultiplier={2}
        {...testProperties(Reports.reportCandidateDateAdded)}>
        Added {formattedDate}
      </Text>
      <Text
        allowFontScaling
        maxFontSizeMultiplier={2}
        variant="button"
        style={[
          styles.sourceStyle,
          styles.inlineSourceText,
          isFocused && styles.focused,
        ]}
        onFocus={onFocus}
        onBlur={onBlur}>
        from {displaySource}
      </Text>
    </View>
  );

  if (onPress) {
    return (
      <TouchableOpacity
        onPress={onPress}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityHint="Double tap to view details"
        {...testProperties(`${Reports.reportCandidateDateAdded}_button`)}>
        <View accessible={false}>{textContent}</View>
      </TouchableOpacity>
    );
  }

  // For non-interactive elements, use a simple accessible View
  return (
    <View
      accessible={true}
      accessibilityRole="text"
      accessibilityLabel={accessibilityLabel}
      {...testProperties(`${Reports.reportCandidateDateAdded}_accessible`)}>
      <View accessible={false}>{textContent}</View>
    </View>
  );
};

export default withTranslation('report.record.dateAddedComponent')(
  DateAddedComponent,
);
